/**
 * # MessageContent — Memoized Markdown Chat Bubble
 *
 * Renders a single Scout chat message as a themed bubble with full markdown support.
 * Designed for streaming AI responses where tokens arrive incrementally.
 *
 * ## Architecture: 3-Layer Memoization
 *
 * This follows the Vercel AI SDK "block-level memoization" pattern
 * (see: ai-sdk.dev/cookbook/next/markdown-chatbot-with-memoization).
 *
 * The problem: when an assistant streams tokens, the message content string changes
 * on every chunk. A naive `<ReactMarkdown>{content}</ReactMarkdown>` would re-parse
 * and re-render the entire markdown tree on every token — O(n) rendering per token,
 * O(n^2) total for a full response.
 *
 * The solution is three layers of memoization:
 *
 * 1. **Message level** (`MessageContent`, `React.memo` with custom comparator):
 *    Completed messages (status: "complete") have stable props — they never re-render
 *    while a different message is streaming. This means only 1 out of N messages in
 *    the conversation re-renders per token.
 *
 * 2. **Block level** (`MemoizedMarkdownBlock`, `React.memo` on each block):
 *    Within the actively streaming message, `marked.lexer()` splits the markdown into
 *    block-level tokens (paragraphs, headings, code fences, etc.). Completed blocks
 *    produce identical raw strings across renders, so `React.memo` skips their
 *    re-render. Only the last (in-progress) block actually re-renders per token.
 *
 * 3. **Component map stability** (`markdownComponents` at module scope):
 *    The `components` prop passed to `react-markdown` is a plain object defined once
 *    at module level — not inside a render function. This prevents `react-markdown`
 *    from seeing a new `components` reference on every render, which would defeat
 *    its own internal memoization.
 *
 * ## Why `marked` + `react-markdown` together?
 *
 * `marked` is used **only** for its lexer (`marked.lexer()`) to split raw markdown
 * into discrete block-level tokens. It does NOT render HTML.
 *
 * `react-markdown` handles all rendering — it parses each block into an AST via
 * remark/rehype and renders it through the MUI component map. This gives us full
 * control over how each markdown element looks (Typography, Box, Link, etc.) while
 * staying in the React tree (no `dangerouslySetInnerHTML`).
 */
import Balance from "@mui/icons-material/Balance";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import MenuBook from "@mui/icons-material/MenuBook";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
  keyframes,
} from "@mui/material";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { marked } from "marked";
import { Children, isValidElement, memo, useMemo, type ReactElement, type ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import {
  useScoutDeepLinksEnabled,
  useScoutDeepLinkUrl,
  useScoutIsDeepLinkTarget,
} from "../../ScoutDeepLinkContext";
import { ScoutDeepLinkCopyButton } from "../../ScoutDeepLinkCopyButton";
import { getScoutDeepLinkTargetSx } from "../../scoutDeepLinks";
import { stripProviderCitationArtifacts } from "../../textSanitization";
import { ScoutChatMessagePart, ScoutReasoningPart, ScoutTextPart } from "./scoutChatMessage";
import {
  CITATION_NO_URL_HREF,
  type CitationIconKind,
  type ScoutCitationSource,
  parseCitationChipTitle,
  transformCitations,
} from "./scoutCitations";
import { useSmoothText } from "./useSmoothText";

const WIDE_TABLE_COLUMN_THRESHOLD = 5;
const WIDE_TABLE_CELL_MIN_WIDTH = "9rem";

function countTableColumns(children?: ReactNode): number {
  let columnCount = 0;
  const visit = (node?: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement<{ children?: ReactNode }>(child)) return;
      if (child.type === "tr") {
        let cellsInRow = 0;
        Children.forEach(child.props.children, (cell) => {
          if (isValidElement(cell)) cellsInRow += 1;
        });
        columnCount = Math.max(columnCount, cellsInRow);
        return;
      }
      visit(child.props.children);
    });
  };
  visit(children);
  return columnCount;
}

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const compactListSx = {
  mt: 0.375,
  mb: 1.125,
  pl: 2.75,
  lineHeight: 1.5,
  "& > li": {
    my: 0.125,
  },
  "& ul, & ol": {
    my: 0.125,
    pl: 2.25,
  },
  "& p": {
    my: 0,
  },
} as const;

const unorderedListSx = {
  ...compactListSx,
  listStyleType: "disc",
  "& ul": {
    listStyleType: "disc",
  },
} as const;

function extractChildrenText(children: ReactNode): string {
  let text = "";
  Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += String(child);
    } else if (isValidElement<{ children?: ReactNode }>(child)) {
      text += extractChildrenText(child.props.children);
    }
  });
  return text;
}

function renderCitationChipIcon(kind: CitationIconKind): ReactElement {
  if (kind === "regulation") {
    return <Balance />;
  }
  if (kind === "link") {
    return <LinkIcon />;
  }
  return <MenuBook />;
}

function CitationChip({
  href,
  label,
  iconKind,
}: {
  href: string | undefined;
  label: string;
  iconKind: CitationIconKind;
}) {
  const isClickable = Boolean(href && href !== CITATION_NO_URL_HREF);
  const commonSx = {
    height: 22,
    mx: 0.25,
    verticalAlign: "baseline",
    bgcolor: "grey.300",
    color: "text.primary",
    fontSize: "0.8125rem",
    "& .MuiChip-icon": {
      fontSize: "1rem",
      ml: 0.5,
      mr: -0.25,
      color: "text.secondary",
    },
    "& .MuiChip-label": {
      px: 0.75,
    },
  } as const;

  if (isClickable && href) {
    return (
      <Chip
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        clickable
        size="small"
        label={label}
        icon={renderCitationChipIcon(iconKind)}
        sx={commonSx}
      />
    );
  }

  return (
    <Chip
      component="span"
      size="small"
      label={label}
      icon={renderCitationChipIcon(iconKind)}
      sx={commonSx}
    />
  );
}

/**
 * Maps markdown element types to MUI components for consistent theming.
 *
 * Defined at **module scope** (not inside a component) so the object reference is
 * stable across renders. If this were created inside a render function,
 * `react-markdown` would receive a new `components` prop every render and bypass
 * its internal memoization — re-rendering the entire markdown tree even when the
 * content string hasn't changed.
 */
const markdownComponents: Components = {
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mt: 0.5, mb: 2.35, lineHeight: 1.58 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children, title }) => {
    const chipKind = parseCitationChipTitle(typeof title === "string" ? title : undefined);
    if (chipKind) {
      const label = extractChildrenText(children);
      return <CitationChip href={href ?? undefined} label={label} iconKind={chipKind} />;
    }

    return (
      <Link
        href={href ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        color="inherit"
        underline="always"
      >
        {children}
      </Link>
    );
  },
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: "grey.900",
        color: "grey.100",
        p: 1.5,
        borderRadius: 1,
        overflowX: "auto",
        mt: 1.5,
        mb: 2,
        fontSize: "0.9375rem",
        lineHeight: 1.6,
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children, className }) => {
    if (className) {
      return <code className={className}>{children}</code>;
    }

    return (
      <Box
        component="code"
        sx={{
          bgcolor: "action.hover",
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: "monospace",
          fontSize: "0.85em",
        }}
      >
        {children}
      </Box>
    );
  },
  ul: ({ children }) => (
    <Box component="ul" sx={unorderedListSx}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={compactListSx}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ whiteSpace: "normal" }}>
      {children}
    </Box>
  ),
  h1: ({ children }) => (
    <Typography component="h1" variant="h6" sx={{ mt: 2.75, mb: 0.9 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography component="h2" variant="h6" sx={{ mt: 2.75, mb: 0.9 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mt: 2.5, mb: 0.65 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography component="h4" variant="subtitle2" fontWeight={600} sx={{ mt: 2.25, mb: 0.65 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: 3,
        borderColor: "primary.main",
        pl: 1.25,
        py: 0.25,
        mt: 1.125,
        mb: 1.75,
        ml: 0,
        opacity: 0.85,
        "& > :first-of-type": {
          mt: 0,
        },
        "& > :last-child": {
          mb: 0,
        },
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: "none", borderTop: 1, borderColor: "divider", mt: 1.75, mb: 2.25 }}
    />
  ),
  table: ({ children }) => {
    const isWide = countTableColumns(children) >= WIDE_TABLE_COLUMN_THRESHOLD;
    const cellMinWidth = isWide ? WIDE_TABLE_CELL_MIN_WIDTH : "min-content";

    return (
      <Box
        sx={{
          mt: 2.5,
          mb: 3.25,
          borderRadius: 3,
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarGutter: "stable",
          WebkitOverflowScrolling: "touch",
          "--scout-table-cell-min-width": cellMinWidth,
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%",
            minWidth: "min-content",
            tableLayout: "auto",
            fontSize: "0.9375rem",
            "& tbody tr:last-of-type td": {
              borderBottom: 0,
            },
            "& thead tr:first-of-type th:first-of-type": {
              borderTopLeftRadius: 3,
            },
            "& thead tr:first-of-type th:last-of-type": {
              borderTopRightRadius: 3,
            },
            "& tbody tr:last-of-type td:first-of-type": {
              borderBottomLeftRadius: 3,
            },
            "& tbody tr:last-of-type td:last-of-type": {
              borderBottomRightRadius: 3,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    );
  },
  th: ({ children }) => (
    <Box
      component="th"
      sx={{
        borderBottom: 1,
        borderColor: "grey.500",
        color: "text.primary",
        fontSize: "0.9375rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        minWidth: "var(--scout-table-cell-min-width)",
        overflowWrap: "normal",
        wordBreak: "normal",
        px: 1.75,
        py: 0.75,
        textAlign: "left",
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box
      component="td"
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        minWidth: "var(--scout-table-cell-min-width)",
        overflowWrap: "normal",
        wordBreak: "normal",
        px: 1.75,
        py: 0.75,
        verticalAlign: "top",
      }}
    >
      {children}
    </Box>
  ),
};

const markdownRemarkPlugins = [remarkGfm, remarkBreaks];

/**
 * Uses `marked.lexer()` to split a markdown string into block-level tokens
 * (paragraphs, headings, code fences, lists, etc.) and returns their raw strings.
 *
 * This is the key to block-level memoization: when a new streaming token arrives,
 * the full content is re-tokenized, but completed blocks produce the exact same
 * raw string as the previous render. `MemoizedMarkdownBlock` uses a string equality
 * check to skip re-rendering those stable blocks.
 *
 * Note: the lexer itself is O(n) per call (it runs on the full string every time).
 * The win is in avoiding O(n) *rendering* for stable blocks — only the last
 * (actively streaming) block's React tree is rebuilt.
 */
function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map((token) => token.raw).filter((block) => block.trim().length > 0);
}

/**
 * Renders a single markdown block via `react-markdown` with MUI component overrides.
 *
 * Wrapped in `React.memo` with a custom comparator that checks string equality on
 * `content`. This is Layer 2 of the memoization strategy: when the parent
 * `MemoizedMarkdown` re-renders (because the streaming message got a new token),
 * blocks whose raw string hasn't changed are skipped entirely.
 *
 * In practice during streaming, all blocks except the last one are stable — so this
 * memo saves us from re-parsing and re-rendering the entire conversation history
 * worth of markdown on every token.
 */
const MemoizedMarkdownBlock = memo(
  function MemoizedMarkdownBlock({ content }: { content: string }) {
    return (
      <Markdown remarkPlugins={markdownRemarkPlugins} components={markdownComponents}>
        {content}
      </Markdown>
    );
  },
  (prev, next) => prev.content === next.content
);
MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

const DeepLinkableMarkdownBlock = memo(
  function DeepLinkableMarkdownBlock({
    content,
    deepLinkId,
  }: {
    content: string;
    deepLinkId: string;
  }) {
    const copyToClipboard = useCopyToClipboard();
    const deepLinkUrl = useScoutDeepLinkUrl(deepLinkId);
    const isDeepLinkTarget = useScoutIsDeepLinkTarget(deepLinkId);

    if (!deepLinkUrl) {
      return <MemoizedMarkdownBlock content={content} />;
    }

    return (
      <Box
        id={deepLinkId}
        tabIndex={-1}
        sx={{
          position: "relative",
          ...getScoutDeepLinkTargetSx(isDeepLinkTarget),
          borderRadius: 1,
          ml: -4,
          pl: 4,
          "& .scout-markdown-block-link-button": {
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 120ms ease-in-out",
          },
          "&:hover .scout-markdown-block-link-button, &:focus-within .scout-markdown-block-link-button":
            {
              opacity: 1,
              pointerEvents: "auto",
            },
        }}
      >
        <MemoizedMarkdownBlock content={content} />
        <ScoutDeepLinkCopyButton
          ariaLabel="Copy link to markdown block"
          tooltip="Copy link to markdown block"
          className="scout-markdown-block-link-button"
          onClick={() => void copyToClipboard(deepLinkUrl)}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            color: "text.secondary",
            bgcolor: "background.paper",
            boxShadow: 1,
            p: 0.25,
            "&:hover": {
              bgcolor: "background.paper",
              color: "text.primary",
            },
          }}
        />
      </Box>
    );
  },
  (prev, next) => prev.content === next.content && prev.deepLinkId === next.deepLinkId
);
DeepLinkableMarkdownBlock.displayName = "DeepLinkableMarkdownBlock";

/**
 * Splits markdown content into block-level tokens and renders each as a memoized
 * block. This is the orchestrator between `parseMarkdownIntoBlocks` (the splitter)
 * and `MemoizedMarkdownBlock` (the per-block renderer).
 *
 * Keys are `${messageId}-block_${index}` — stable for a given message as long as
 * blocks aren't inserted above (which doesn't happen during streaming since tokens
 * append to the end).
 *
 * The `useMemo` on `parseMarkdownIntoBlocks` ensures the lexer only runs when the
 * content string actually changes, not on unrelated parent re-renders.
 */
const MemoizedMarkdown = memo(function MemoizedMarkdown({
  content,
  blockKey,
  enableBlockDeepLinks = false,
}: {
  content: string;
  blockKey: string;
  enableBlockDeepLinks?: boolean;
}) {
  const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

  return (
    <>
      {blocks.map((block, index) => {
        const key = `${blockKey}-${index}`;
        if (!enableBlockDeepLinks) {
          return <MemoizedMarkdownBlock key={key} content={block} />;
        }

        return (
          <DeepLinkableMarkdownBlock
            key={key}
            content={block}
            deepLinkId={`${blockKey}-block-${index}`}
          />
        );
      })}
    </>
  );
});
MemoizedMarkdown.displayName = "MemoizedMarkdown";

/**
 * Renders a markdown segment with an optional typewriter reveal.
 *
 * `useSmoothText` is always called (rules-of-hooks) but only animates when
 * `isActive` — i.e. this is the trailing, actively-streaming markdown segment.
 * Inactive segments (completed text, non-streaming history) pass `enabled:
 * false` and get the full text instantly, so behavior is unchanged for anything
 * that isn't the live stream tail.
 *
 * Block-level memoization still applies underneath: the revealed prefix is fed
 * to `MemoizedMarkdown`, so per-frame re-lexing cost stays bounded to the
 * trailing block — the same cost profile as today's per-token re-lex.
 */
const SmoothMarkdownSegment = memo(function SmoothMarkdownSegment({
  content,
  blockKey,
  isActive,
  enableBlockDeepLinks = false,
}: {
  content: string;
  blockKey: string;
  isActive: boolean;
  enableBlockDeepLinks?: boolean;
}) {
  const revealedContent = useSmoothText(content, { enabled: isActive });
  return (
    <MemoizedMarkdown
      content={revealedContent}
      blockKey={blockKey}
      enableBlockDeepLinks={enableBlockDeepLinks}
    />
  );
});
SmoothMarkdownSegment.displayName = "SmoothMarkdownSegment";

type RenderableTextPart = ScoutTextPart | ScoutReasoningPart;

function isThinkingPart(part: RenderableTextPart): boolean {
  return part.type === "reasoning" || part.agentState === "thinking";
}

/**
 * Local render-only shape for preserving the existing text render modes while
 * still merging provider text fragments before markdown parsing.
 *
 * Renderer contract: every visible `type: "text"` part whose `agentState` is
 * not `"thinking"` is customer-facing markdown. Normal assistant text parts are
 * stream fragments, so adjacent fragments need to be concatenated into one
 * markdown document before `marked.lexer()` sees them. Reasoning parts and
 * thinking-state text parts are intentionally not markdown-rendered
 * customer-facing prose, so they stay as their own segment and render through
 * Typography.
 *
 * Toy example:
 * ```
 * [markdown: "## Answer\n\n| A | B |\n|---|---|\n| 1"]
 * [markdown: " | 2 |"]
 * [thinking: "## Thinking\n\n**Checking context**"]
 * [markdown: "\n\n- Done"]
 * ```
 * becomes:
 * ```
 * [markdown: "## Answer\n\n| A | B |\n|---|---|\n| 1 | 2 |"]
 * [thinking: "## Thinking\n\n**Checking context**"]
 * [markdown: "\n\n- Done"]
 * ```
 */
type TextRenderSegment =
  | { kind: "markdown"; id: string; text: string }
  | { kind: "thinking"; id: string; part: RenderableTextPart }
  | { kind: "sources"; id: string; sources: ScoutCitationSource[] };

/**
 * Builds contiguous render segments from persisted Scout text parts.
 *
 * This keeps the optimization boundary at `MemoizedMarkdownBlock`: each merged
 * markdown segment is still split by `marked.lexer()` into memoized markdown
 * blocks, but split provider text parts no longer become independent markdown
 * documents that can break tables.
 *
 * In practice this handles provider streams that interleave normal answer text
 * and thinking text in one agent generation:
 * ```
 * answer fragment -> answer fragment -> thinking fragment -> answer fragment
 * ```
 * The two answer fragments before thinking are parsed as one markdown document,
 * the thinking fragment renders literally, and the final answer fragment starts
 * a new markdown document.
 */
function buildTextRenderSegments(parts: RenderableTextPart[]): TextRenderSegment[] {
  const segments: TextRenderSegment[] = [];

  for (const part of parts) {
    if (isThinkingPart(part)) {
      segments.push({ kind: "thinking", id: part.id, part });
      continue;
    }

    const text = stripProviderCitationArtifacts(part.text);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment?.kind === "markdown") {
      lastSegment.text += text;
    } else {
      segments.push({ kind: "markdown", id: part.id, text });
    }
  }

  // Rewrite `[N]` inline markers into chip-friendly markdown, and split the
  // `## Sources` block off into its own render segment so the caller can show
  // it as a collapsible accordion. Runs on merged segment text so a Sources
  // block that spans stream fragments still parses in one pass.
  const rewrittenSegments: TextRenderSegment[] = [];
  for (const segment of segments) {
    if (segment.kind !== "markdown") {
      rewrittenSegments.push(segment);
      continue;
    }
    const { markdown, sources } = transformCitations(segment.text);
    rewrittenSegments.push({ ...segment, text: markdown });
    if (sources && sources.length > 0) {
      rewrittenSegments.push({ kind: "sources", id: `${segment.id}-sources`, sources });
    }
  }

  return rewrittenSegments;
}

const ScoutSourcesAccordion = memo(
  function ScoutSourcesAccordion({ sources }: { sources: ScoutCitationSource[] }) {
    if (sources.length === 0) {
      return null;
    }
    return (
      <Accordion
        disableGutters
        elevation={0}
        square
        sx={{
          mt: 1.5,
          bgcolor: "transparent",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          "&::before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize="small" />}
          sx={{
            minHeight: 36,
            px: 1.5,
            flexDirection: "row-reverse",
            gap: 0.75,
            "& .MuiAccordionSummary-expandIconWrapper": {
              transform: "rotate(-90deg)",
              transition: "transform 150ms ease",
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(0deg)",
            },
            "& .MuiAccordionSummary-content": {
              my: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            Sources
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
          >
            {sources.length} {sources.length === 1 ? "source" : "sources"} cited
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 1.25, px: 1.5 }}>
          <Stack spacing={0.75} alignItems="flex-start">
            {sources.map((source) => (
              <CitationChip
                key={`source-${source.number}`}
                href={source.url}
                label={source.title}
                iconKind={source.iconKind}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  },
  (prev, next) => {
    if (prev.sources.length !== next.sources.length) return false;
    for (let i = 0; i < prev.sources.length; i += 1) {
      const a = prev.sources[i];
      const b = next.sources[i];
      if (
        a.number !== b.number ||
        a.title !== b.title ||
        a.url !== b.url ||
        a.iconKind !== b.iconKind
      ) {
        return false;
      }
    }
    return true;
  }
);
ScoutSourcesAccordion.displayName = "ScoutSourcesAccordion";

const ThinkingTextPart = memo(
  function ThinkingTextPart({ part }: { part: RenderableTextPart }) {
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          my: 0,
          lineHeight: 1.4,
          whiteSpace: "pre-line",
          transform: "translateY(3px)",
        }}
      >
        {part.text}
      </Typography>
    );
  },
  (prev, next) =>
    prev.part.id === next.part.id &&
    prev.part.text === next.part.text &&
    prev.part.agentState === next.part.agentState
);
ThinkingTextPart.displayName = "ThinkingTextPart";

export const MessageContent = memo(function MessageContent({
  messageId,
  parts,
  isStreaming,
}: {
  messageId: string;
  parts: ScoutChatMessagePart[];
  isStreaming?: boolean;
}) {
  const visibleTextParts = useMemo(
    () =>
      parts.filter(
        (part): part is RenderableTextPart => part.type === "text" || part.type === "reasoning"
      ),
    [parts]
  );
  const textRenderSegments = useMemo(
    () => buildTextRenderSegments(visibleTextParts),
    [visibleTextParts]
  );
  // Only the last markdown segment can be the live stream tail; everything
  // before it is settled text that should render fully (no reveal animation).
  const lastMarkdownIndex = useMemo(
    () =>
      textRenderSegments.reduce(
        (last, segment, index) => (segment.kind === "markdown" ? index : last),
        -1
      ),
    [textRenderSegments]
  );
  const hasRenderableText = textRenderSegments.some((segment) => {
    if (segment.kind === "thinking") {
      return segment.part.text.length > 0;
    }
    if (segment.kind === "sources") {
      return segment.sources.length > 0;
    }
    return segment.text.length > 0;
  });
  const deepLinksEnabled = useScoutDeepLinksEnabled();
  const shouldEnableMarkdownBlockDeepLinks = Boolean(deepLinksEnabled && !isStreaming);

  if (hasRenderableText) {
    return (
      <Box
        component="div"
        sx={{
          "& > :first-of-type, & > :first-of-type > :first-of-type": {
            mt: 0,
          },
          "& > h1:first-of-type, & > h2:first-of-type, & > h3:first-of-type, & > h4:first-of-type, & > :first-of-type > h1:first-of-type, & > :first-of-type > h2:first-of-type, & > :first-of-type > h3:first-of-type, & > :first-of-type > h4:first-of-type":
            {
              transform: "translateY(-2px)",
            },
          "& > :last-child, & > :last-child > :last-child": {
            mb: 0,
          },
        }}
      >
        {textRenderSegments.map((segment, index) => {
          if (segment.kind === "thinking") {
            return <ThinkingTextPart key={`${messageId}-${segment.id}`} part={segment.part} />;
          }

          if (segment.kind === "sources") {
            return (
              <ScoutSourcesAccordion key={`${messageId}-${segment.id}`} sources={segment.sources} />
            );
          }

          const isActive = Boolean(isStreaming) && index === lastMarkdownIndex;

          return (
            <SmoothMarkdownSegment
              key={`${messageId}-${segment.id}`}
              content={segment.text}
              blockKey={segment.id}
              isActive={isActive}
              enableBlockDeepLinks={shouldEnableMarkdownBlockDeepLinks}
            />
          );
        })}
      </Box>
    );
  }

  if (isStreaming) {
    return (
      <Typography variant="body1">
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "2px",
            height: "1em",
            bgcolor: "text.primary",
            ml: 0.25,
            verticalAlign: "text-bottom",
            animation: `${blink} 1s step-end infinite`,
          }}
        />
      </Typography>
    );
  }

  return null;
});
MessageContent.displayName = "MessageContent";
